import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { Button } from '@yoast/ui-library';
import cx from 'classnames';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import type {
  AnalysisBase,
  AnalysisIdOrLatest,
  DateTime,
} from '../../api/generated/insights.schemas';

// Calendar color theme constants
const CALENDAR_COLORS = {
  // Selected date colors
  selected: {
    background: 'yst-bg-primary-500',
    text: 'yst-text-white',
    hover: 'hover:yst-bg-primary-600',
  },
  // Has analysis colors
  hasAnalysis: {
    border: 'yst-border-2 yst-border-primary-500',
    text: 'yst-text-slate-900',
    hover: 'hover:yst-bg-primary-50',
  },
  // Today indicator (when not selected)
  today: {
    text: 'yst-text-slate-900',
    fontWeight: 'yst-font-normal',
    underline: 'yst-border-b-2 yst-border-slate-400',
  },
  // Analysis count indicator
  indicator: {
    selected: 'yst-bg-white',
    default: 'yst-bg-blue-500',
  },
} as const;

interface AnalysisCalendarPickerProps {
  analyses: AnalysisBase[];
  selectedAnalysisId: AnalysisIdOrLatest;
  onAnalysisSelect: (analysisId: AnalysisIdOrLatest) => void;
  isLoading?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const AnalysisCalendarPicker = ({
  analyses,
  selectedAnalysisId,
  onAnalysisSelect,
  isLoading = false,
  isOpen = false,
  onClose,
}: AnalysisCalendarPickerProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Helper to create a local Date from the ISO string by using only the date portion (YYYY-MM-DD)
  // Prevents timezone offsets from shifting the day when displaying/aggregating by date
  const getLocalDateFromCreatedAt = (createdAt: DateTime): Date | null => {
    if (!createdAt) return null;
    const ymd = createdAt.slice(0, 10); // 'YYYY-MM-DD'
    const [yearStr, monthStr, dayStr] = ymd.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    return new Date(year, month - 1, day);
  };

  // Create a map of dates to analysis data
  const analysisMap = useMemo(() => {
    const map = new Map<string, AnalysisBase[]>();
    analyses.forEach((analysis) => {
      const localDate = getLocalDateFromCreatedAt(analysis.createdAt!);
      const dateKey = localDate ? format(localDate, 'yyyy-MM-dd') : '';
      if (!dateKey) return;
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(analysis);
    });
    return map;
  }, [analyses]);

  // Set current month to the selected analysis date when opened
  useEffect(() => {
    if (isOpen && selectedAnalysisId) {
      const selectedAnalysis = analyses.find(
        (a) => a.id === selectedAnalysisId,
      );
      if (selectedAnalysis) {
        const localDate = getLocalDateFromCreatedAt(
          selectedAnalysis.createdAt!,
        );
        if (localDate) setCurrentMonth(localDate);
      }
    } else if (isOpen && analyses.length > 0) {
      // Default to most recent analysis if none selected
      const mostRecent = analyses.reduce((latest, analysis) => {
        return new Date(analysis.createdAt!) > new Date(latest.createdAt!)
          ? analysis
          : latest;
      });
      const localDate = getLocalDateFromCreatedAt(mostRecent.createdAt!);
      if (localDate) setCurrentMonth(localDate);
    }
  }, [isOpen, selectedAnalysisId, analyses]);

  // Find the most recent analysis
  const mostRecentAnalysis = useMemo(() => {
    if (analyses.length === 0) return null;
    return analyses.reduce((latest, analysis) => {
      return new Date(analysis.createdAt!) > new Date(latest.createdAt!)
        ? analysis
        : latest;
    });
  }, [analyses]);

  const handleShowLatestAnalysis = () => {
    if (mostRecentAnalysis) {
      // Set the month to the latest analysis
      const localDate = getLocalDateFromCreatedAt(
        mostRecentAnalysis.createdAt!,
      );
      if (localDate) setCurrentMonth(localDate);

      // Do not select the latest analysis - just navigate to its month
      // onAnalysisSelect(mostRecentAnalysis.id);

      // Do not close the calendar - keep it open for user to select
      // onClose?.();
    }
  };

  // Check if the latest analysis is already visible in the current month
  const isLatestAnalysisInCurrentMonth = useMemo(() => {
    if (!mostRecentAnalysis) return false;
    const localDate = getLocalDateFromCreatedAt(mostRecentAnalysis.createdAt!);
    return localDate ? isSameMonth(localDate, currentMonth) : false;
  }, [mostRecentAnalysis, currentMonth]);

  // Generate calendar days for current month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    // Get the first day of the week for the calendar (start from Monday)
    const startDate = new Date(monthStart);
    const dayOfWeek = startDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - mondayOffset);

    // Get the last day needed for the calendar grid
    const endDate = new Date(monthEnd);
    const endDayOfWeek = endDate.getDay();
    const sundayOffset = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + sundayOffset);

    return eachDayOfInterval({ start: startDate, end: endDate }).map((date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      const dayAnalyses = analysisMap.get(dateString) || [];
      const selectedAnalysis = dayAnalyses.find(
        (a) => String(a.id) === String(selectedAnalysisId),
      );

      return {
        date: dateString,
        dateObj: date,
        isCurrentMonth: isSameMonth(date, currentMonth),
        isToday: isToday(date),
        isSelected: !!selectedAnalysis,
        hasAnalyses: dayAnalyses.length > 0,
        analyses: dayAnalyses,
      };
    });
  }, [currentMonth, analysisMap, selectedAnalysisId]);

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleDayClick = (day: (typeof calendarDays)[0]) => {
    if (day.hasAnalyses) {
      // If there's only one analysis for this day, select it directly
      if (day.analyses.length === 1) {
        onAnalysisSelect(day.analyses[0].id!);
      } else {
        // If multiple analyses exist for this day, select the most recent one
        const mostRecentAnalysis = day.analyses.reduce((latest, analysis) => {
          return new Date(analysis.createdAt!) > new Date(latest.createdAt!)
            ? analysis
            : latest;
        });
        onAnalysisSelect(mostRecentAnalysis.id!);
      }
      onClose?.();
    }
  };

  if (!isOpen) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="yst-absolute yst-left-0 yst-right-0 yst-top-full yst-z-50 yst-mt-1 yst-rounded-md yst-border yst-border-slate-200 yst-bg-white yst-p-4 yst-shadow-lg">
        <div className="yst-animate-pulse yst-space-y-4">
          <div className="yst-h-6 yst-rounded yst-bg-slate-200"></div>
          <div className="yst-h-48 yst-rounded yst-bg-slate-200"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="yst-absolute yst-left-0 yst-right-0 yst-top-full yst-z-50 yst-mt-1 yst-min-w-80 yst-max-w-sm yst-rounded-md yst-border yst-border-slate-200 yst-bg-white yst-p-4 yst-shadow-lg">
      {/* Calendar Header */}
      <div className="yst-mb-6 yst-flex yst-items-center yst-justify-between">
        <button
          type="button"
          onClick={handlePreviousMonth}
          className="yst-rounded-md yst-p-1.5 yst-text-slate-400 hover:yst-bg-slate-100 hover:yst-text-slate-500"
        >
          <span className="yst-sr-only">Previous month</span>
          <ChevronLeftIcon className="yst-h-5 yst-w-5" />
        </button>

        <h2 className="yst-text-sm yst-font-semibold yst-text-slate-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>

        <button
          type="button"
          onClick={handleNextMonth}
          className="yst-rounded-md yst-p-1.5 yst-text-slate-400 hover:yst-bg-slate-100 hover:yst-text-slate-500"
        >
          <span className="yst-sr-only">Next month</span>
          <ChevronRightIcon className="yst-h-5 yst-w-5" />
        </button>
      </div>

      {/* Days of Week Header */}
      <div className="yst-mb-0 yst-grid yst-grid-cols-7 yst-gap-x-1">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
          <div
            key={`${day}-${idx}`}
            className="yst-py-2 yst-text-center yst-text-xs yst-font-medium yst-text-slate-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="yst-grid yst-grid-cols-7 yst-gap-x-1">
        {calendarDays.map((day) => (
          <div
            key={day.date}
            className="yst-flex yst-items-center yst-justify-center yst-py-1"
          >
            <button
              type="button"
              onClick={() => handleDayClick(day)}
              disabled={!day.hasAnalyses}
              className={cx(
                'yst-relative yst-rounded-full yst-text-sm yst-transition-colors',
                // Base styles - ensure perfect circle with equal width and height
                'yst-flex yst-h-10 yst-w-10 yst-items-center yst-justify-center',
                // Remove padding to maintain circle shape
                'yst-p-0',
                // Current month vs other months
                {
                  'yst-text-slate-900': day.isCurrentMonth,
                  'yst-text-slate-400': !day.isCurrentMonth,
                },
                // Hover and focus states
                {
                  'focus:yst-outline-none focus:yst-ring-2 focus:yst-ring-blue-500':
                    day.hasAnalyses,
                  'yst-cursor-not-allowed': !day.hasAnalyses,
                  'yst-cursor-pointer': day.hasAnalyses,
                },
                // Selected state
                {
                  [`${CALENDAR_COLORS.selected.background} ${CALENDAR_COLORS.selected.text} ${CALENDAR_COLORS.selected.hover}`]:
                    day.isSelected,
                },
                // Has analyses indicator
                {
                  [`${CALENDAR_COLORS.hasAnalysis.border} ${CALENDAR_COLORS.hasAnalysis.text} ${CALENDAR_COLORS.hasAnalysis.hover}`]:
                    day.hasAnalyses && !day.isSelected,
                },
              )}
            >
              <span
                className={cx(
                  'yst-relative yst-z-10 yst-w-4',
                  // Today indicator - add underline
                  {
                    [CALENDAR_COLORS.today.underline]:
                      day.isToday && !day.isSelected,
                  },
                )}
              >
                {format(day.dateObj, 'd')}
              </span>

              {/* Analysis count indicator */}
              {day.hasAnalyses && day.analyses.length > 1 && !day.isSelected && (
                <span
                  className={cx(
                    'yst-absolute yst-right-1 yst-top-1 yst-h-2 yst-w-2 yst-rounded-full',
                    CALENDAR_COLORS.indicator.default,
                  )}
                />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="yst-mt-4 yst-flex yst-items-center yst-gap-4 yst-text-xs yst-text-slate-600">
        <div className="yst-flex yst-items-center yst-gap-1">
          <div
            className={`yst-h-3 yst-w-3 yst-rounded-full ${CALENDAR_COLORS.hasAnalysis.border}`}
          ></div>
          <span>Has analysis</span>
        </div>
        <div className="yst-flex yst-items-center yst-gap-1">
          <div
            className={`yst-h-3 yst-w-3 yst-rounded-full ${CALENDAR_COLORS.selected.background}`}
          ></div>
          <span>Selected</span>
        </div>
        <div className="yst-flex yst-items-center yst-gap-1">
          <div className="yst-h-0.5 yst-w-4 yst-bg-slate-400"></div>
          <span>Today</span>
        </div>
      </div>

      {/* Show latest analysis button */}
      {mostRecentAnalysis && (
        <div className="yst-mt-6">
          <Button
            onClick={handleShowLatestAnalysis}
            className="yst-w-full"
            disabled={isLatestAnalysisInCurrentMonth}
          >
            Show latest analysis
            {/* ({format(parseISO(mostRecentAnalysis.createdAt), 'MMM d, yyyy')}) */}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AnalysisCalendarPicker;
